import React from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import {
  ArrowRight, Building, Globe, Leaf, TrendingUp,
  Users, Zap, CheckCircle2
} from 'lucide-react';
import { useInView } from '../components/UI';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const fadeLeft: Variants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const heroStats = [
  { value: '$3.78B', label: 'Africa motorcycle market 2024' },
  { value: '$6.38B', label: 'Projected market size 2033' },
  { value: '+40%', label: 'YoY e-moto sales growth 2024' },
  { value: '60%+', label: 'Nigeria projected EV penetration 2040' },
];

const forces = [
  { icon: Users, num: '2.5B', numLabel: 'Projected Africa population by 2050', title: 'Population & Urbanisation', desc: 'Africa is home to the world\'s fastest-growing population. As cities expand, transportation demand grows proportionally, creating sustained long-term demand for scalable mobility infrastructure.', img: '/images/bike1.png' },
  { icon: Zap, num: '4–6×', numLabel: 'Fuel price surge after subsidy removal', title: 'The Fuel Subsidy Shock', desc: 'Nigeria\'s removal of petrol subsidies in 2023 caused fuel prices to surge overnight. For 8M+ commercial motorcycle operators, fuel now consumes 30–50% of daily earnings. The economics of petrol are broken.', img: '/images/bike2.png' },
  { icon: TrendingUp, num: '40%', numLabel: 'YoY e-moto growth, Africa 2024', title: 'The Electric Mobility Moment', desc: 'The global shift toward two-wheeler adoption is already accelerating across the continent. In Kenya, e-motos reached 15.3% of new registrations in 2025, up from 0.5% in 2021. The market is not theoretical. It is already moving.', img: '/images/bike3.png' },
  { icon: Leaf, num: '$450B+', numLabel: 'Annual global climate finance flows', title: 'Climate Finance Opportunity', desc: 'The global shift toward sustainable infrastructure has unlocked significant capital through DFIs, carbon markets, ESG investment mandates, and climate grants. African clean mobility is exactly what this capital is looking for.', img: '/images/bike4.png' },
  { icon: Building, num: '25M', numLabel: 'Motorcycles in Africa, built nowhere', title: 'The Manufacturing Gap', desc: 'Africa has approximately 25 million motorcycles in active use, yet manufactures almost none of them. Every purchase is a capital outflow. Azura intends to fix this permanently through Sankofa Foundry.', img: '/images/bike5.png' },
  { icon: Globe, num: 'No.1', numLabel: 'Uncontested EV launch market in Nigeria', title: 'Ibadan: The Perfect Launchpad', desc: "Nigeria's third-largest city with 3.6M people, no Okada ban, no dominant EV competitor, and validated $6/day remittance economics. What Rwanda was to Spiro, Ibadan is to Azura.", img: '/images/bike6.png' },
];

const marketData = [
  { label: 'Africa motorcycle market (2024)', value: '$3.78B' },
  { label: 'Projected market size (2033)', value: '$6.38B' },
  { label: 'African e-moto market (2023)', value: '$441M' },
  { label: 'Projected e-moto market (2031)', value: '$2.6B' },
  { label: 'YoY growth in e-moto sales (2024)', value: '+40%' },
  { label: "Nigeria's share of Africa motorcycle market", value: '28.4%' },
  { label: 'Projected Nigeria EV penetration (2040)', value: '60%+' },
];

const nigeriaStats = [
  { v: '220M', l: 'Population (largest in Africa)' },
  { v: '8M+', l: 'Commercial motorcycle operators' },
  { v: '28.4%', l: 'Share of Africa motorcycle market' },
  { v: '60%+', l: 'Government EV target by 2040' },
];


const ibadanPoints = [
  { title: 'Scale', desc: 'Population ~3.6M, large enough to validate the model at real commercial scale, yet manageable enough to build operational excellence without the complexity of Lagos.' },
  { title: 'Legal Status', desc: 'Unlike Lagos, Ibadan has no ban on commercial motorcycles. The okada economy is active, deeply embedded, and provides an immediate customer base for fleet deployment.' },
  { title: 'First-Mover', desc: 'The competitive window is open. No well-capitalised EV fleet operator has prioritised Ibadan. Azura enters as the first mover with the ability to define the market before competition arrives.' },
  { title: 'Validated Economics', desc: 'The remittance model was validated through direct field research in Ibadan, stress-tested against real rider income patterns and operating costs.' },
  { title: 'Policy Environment', desc: 'Adjacent Ogun State has already deployed 2,000 e-motos with a $40M state investment, setting a precedent for the region.' },
  { title: 'Expansion Path', desc: 'Phase 1 Ibadan → National Nigeria → Pan-African → Continental OEM. A proven model before entering complex markets.' },
];

const phases = [
  { phase: 'Phase 1', name: 'Ibadan', desc: '500 bikes, 17 BaaS stations. Validate the model, build excellence, establish the brand.', period: '2026–2027' },
  { phase: 'Phase 2', name: 'National Nigeria', desc: 'Lagos, Abuja, Port Harcourt, Kano. Azura Ride launches. 5,000–20,000 EVs deployed.', period: '2028–2030' },
  { phase: 'Phase 3', name: 'Pan-African', desc: '5+ African markets. 100,000+ EVs. Regional BaaS hubs. Carbon programmes active.', period: '2031–2036' },
  { phase: 'Phase 4', name: 'Continental OEM', desc: '500,000 EVs. $1.28B+ total revenue. Sankofa Foundry at full OEM scale.', period: '2037–2045' },
];

const Img: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => (
  <img src={src} alt={alt} className={className}
    onError={e => { const t = e.target as HTMLImageElement; t.src = ''; t.parentElement!.style.background = 'linear-gradient(135deg,#1E3A2F,#2B5243)'; }} />
);

/* ══ WHY AFRICA PAGE ══ */
const WhyAfrica: React.FC = () => {
  const { ref: statsRef, inView: statsInView } = useInView();
  const { ref: forcesRef, inView: forcesInView } = useInView();
  const { ref: mktRef, inView: mktInView } = useInView();
  const { ref: ngRef, inView: ngInView } = useInView();
  const { ref: ibRef, inView: ibInView } = useInView();
  const { ref: phaseRef, inView: phaseInView } = useInView();

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">


            <section className="relative bg-bg-dark overflow-hidden">
      
              <div className="absolute inset-0">
                <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
      
                <div
                  className="absolute inset-0 opacity-[0.04]"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)",
                    backgroundSize: "70px 70px",
                  }}
                />
              </div>
      
              <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
      
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
                  <Link
                    to="/"
                    className="text-white/60 hover:text-accent-amber transition text-xs font-semibold"
                  >
                    Home
                  </Link>
      
                  <span className="text-white/30">/</span>
      
                  <span className="text-accent-amber text-xs font-semibold">
                    Why Africa
                  </span>
                </div>
      
                <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl">
                  Why Africa
                  <span className="text-accent-amber"> Why Now</span>
                </h1>
      
                <p className="mt-6 max-w-2xl text-white/70 text-md leading-8">
                    Africa has one of the world's fastest-growing mobility markets, creating a unique opportunity to build affordable, sustainable, and locally driven transportation solutions.

                </p>
      
              </div>
      
            </section>


      {/* ══ HERO ══ */}
      <section className="bg-bg-cream px-6 pb-0 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full border border-white/[0.05]" />
          <div className="absolute top-1/2 -left-20 w-64 h-64 bg-accent-amber opacity-[0.06]" style={{ borderRadius: '60% 40% 55% 45% / 50% 60% 40% 55%' }} />
          <div className="absolute bottom-0 right-16 w-40 h-24 opacity-[0.1]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <motion.div initial="hidden" animate="visible" variants={stagger} className="lg:col-span-6 pt-14 pb-16">

              <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
                <div className="flex items-center -space-x-1">
                  <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                  <div className="w-3.5 h-3.5 rounded-full bg-white/20 border-l border-bg-dark" />
                </div>
                <span className="text-xs font-bold uppercase tracking-[0.2em]">Market Opportunity</span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="font-figtree font-black text-5xl md:text-6xl leading-[1.05] tracking-tight mb-5">
                Why Africa.<br />
                <span className="text-accent-amber italic">Why now.</span>
              </motion.h1>

              <motion.p variants={fadeUp} className="font-exo text-base leading-relaxed max-w-lg mb-10">
                Africa represents one of the most consequential mobility opportunities of the 21st century. A convergence of population growth, fuel price shock, climate capital, and an uncontested manufacturing gap makes 2026 not just a good time to move. It is the right time.
              </motion.p>

              {/* hero stat grid */}
              <motion.div ref={statsRef} variants={fadeUp} className="grid grid-cols-2 gap-3">
                {heroStats.map((s, i) => (
                  <div key={s.label}
                    className={`p-5 rounded-2xl border border-white/[0.08] bg-gray-100 transition-all duration-700 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${i * 80}ms` }}>
                    <p className="font-figtree font-black text-2xl text-accent-amber mb-1 tracking-tight">{s.value}</p>
                    <p className="text-xs leading-snug">{s.label}</p>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* hero image flush bottom */}
            <motion.div initial="hidden" animate="visible" variants={fadeLeft} className="lg:col-span-6 self-end">
              <div className="relative rounded-t-3xl overflow-hidden aspect-[4/3]">
                <Img src="/images/bike7.png" alt="Africa mobility market" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 via-bg-dark/10 to-transparent" />
              
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ SIX CONVERGING FORCES ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={forcesRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${forcesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Six Converging Forces</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              The conditions that make<br />
              <span className="text-accent-amber italic">this moment irreversible.</span>
            </h2>
          </div>

          <div className="space-y-8">
            {forces.map((f, i) => {
              const Icon = f.icon;
              const isEven = i % 2 === 0;
              return (
                <div key={f.title} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${forcesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} transition-all duration-700`}
                  style={{ transitionDelay: `${i * 80}ms` }}>

                  {/* image — alternates sides */}
                  <div className={`lg:col-span-4 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-card border border-border group">
                      <Img src={f.img} alt={f.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                      <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <p className="font-figtree font-black text-3xl text-accent-amber mb-0.5">{f.num}</p>
                        <p className="text-xs text-white/50 uppercase tracking-widest">{f.numLabel}</p>
                      </div>
                      <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-bg-dark/80 backdrop-blur border border-white/10 flex items-center justify-center text-accent-amber">
                        <Icon size={18} />
                      </div>
                    </div>
                  </div>

                  {/* text */}
                  <div className={`lg:col-span-8 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="bg-white border border-border rounded-2xl p-8 shadow-card relative overflow-hidden">
                      <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent-amber" />
                      <span className="absolute bottom-5 right-6 font-figtree font-black text-[5rem] leading-none text-bg-dark/[0.04] select-none pointer-events-none">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-neutral-50 border border-border flex items-center justify-center text-bg-dark">
                          <Icon size={18} />
                        </div>
                        <h3 className="font-figtree font-black text-xl text-bg-dark tracking-tight">{f.title}</h3>
                      </div>
                      <p className="font-exo text-sm text-neutral-600 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ MARKET DATA ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={mktRef} className="max-w-7xl mx-auto">
          <div className={`mb-12 transition-all duration-700 ${mktInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Market Data</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              The numbers behind <span className="text-accent-amber italic">the opportunity.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* table */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
                <div className="border-b border-border px-6 py-4 grid grid-cols-2 bg-neutral-50">
                  <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Metric</span>
                  <span className="text-xs font-bold uppercase tracking-widest text-neutral-500">Value</span>
                </div>
                {marketData.map((row, i) => (
                  <div key={i}
                    className={`px-6 py-4 grid grid-cols-2 border-b border-border last:border-0 transition-all duration-500 hover:bg-neutral-50 ${mktInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                    style={{ transitionDelay: `${i * 60}ms` }}>
                    <span className="font-exo text-sm text-neutral-600">{row.label}</span>
                    <span className="font-figtree font-black text-accent-amber text-sm">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* image */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/2] shadow-dark border border-border group">
                <Img src="/images/bike1.png" alt="Africa market" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/80 to-transparent" />
                
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ NIGERIA ══ */}
      <section className="py-20 px-6 bg-bg-dark text-white relative overflow-hidden border-t-4 border-accent-amber">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
          <div className="absolute bottom-10 left-10 w-40 h-24 opacity-[0.08]" style={{ background: 'radial-gradient(#FFB800 1.5px, transparent 1.5px)', backgroundSize: '10px 10px' }} />
        </div>

        <div  className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center ">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] shadow-dark border border-white/10 group">
                <Img src="/images/bike2.png" alt="Nigeria market" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/80 to-transparent" />
               
              </div>
            </motion.div>

            <div className={`lg:col-span-7 transition-all duration-700  opacity-100 translate-x-0`}>
         
              <h2 className="font-figtree font-black text-4xl md:text-5xl text-white tracking-tight mb-5">
                Africa's largest economy.<br />
                <span className="text-accent-amber italic">Its most urgent mobility need.</span>
              </h2>
              <p className="font-exo text-white/60 text-sm leading-relaxed mb-5">
                Nigeria is the natural anchor market for Azura. With 220 million people, the continent's largest GDP, and over 8 million commercial motorcycle operators, the scale of the problem and the scale of the opportunity are both without parallel.
              </p>
              <p className="font-exo text-white/60 text-sm leading-relaxed mb-8">
                The petrol subsidy removal of 2023 created an inflection point. Fuel prices rose 4–6× overnight. Riders who were marginally profitable became economically stressed. The government's own EV policy target of 60%+ penetration by 2040 signals where the regulatory tailwind is blowing.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 border-y border-white/10 py-6">
                {nigeriaStats.map(s => (
                  <div key={s.l}>
                    <p className="font-figtree font-black text-2xl text-accent-amber mb-1">{s.v}</p>
                    <p className="text-xs text-white/80">{s.l}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ══ IBADAN LAUNCH MARKET ══ */}
      <section className="py-20 px-6 bg-white border-b border-border">
        <div ref={ibRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${ibInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">The Launch Market</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight mb-5">
              Why Ibadan <span className="text-accent-amber italic">and not Lagos?</span>
            </h2>

            <div className="flex gap-5 p-7 rounded-2xl bg-neutral-50 border border-border max-w-3xl">
              <div className="w-[3px] self-stretch bg-accent-amber rounded-full shrink-0" />
              <div>
                <p className="font-exo text-base text-bg-dark leading-relaxed">Ibadan offers Azura what Rwanda offered Spiro: a real market of sufficient scale to validate, learn, and build operational excellence before entering more competitive environments.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeLeft} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden aspect-[3/4] shadow-dark border border-border group">
                <Img src="/images/bike3.png" alt="Ibadan Nigeria" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 to-transparent" />
               
              </div>
            </motion.div>

            {/* checklist */}
            <div className="lg:col-span-7 space-y-3">
              {ibadanPoints.map((p, i) => (
                <div key={i}
                  className={`flex items-start gap-3.5 p-5 rounded-2xl bg-white border border-border shadow-card hover:border-bg-dark/25 hover:shadow-card-hover transition-all duration-200 ${ibInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                  style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="w-5 h-5 rounded-full bg-accent-amber-light border border-accent-amber/30 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle2 size={11} className="text-bg-dark" />
                  </div>
                  <div>
                    <p className="font-figtree font-bold text-sm text-bg-dark mb-1">{p.title}</p>
                    <p className="font-exo text-xs text-neutral-500 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ EXPANSION PHASES ══ */}
      <section className="py-20 px-6 bg-bg-cream">
        <div ref={phaseRef} className="max-w-7xl mx-auto">
          <div className={`mb-14 transition-all duration-700 ${phaseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center -space-x-1">
                <div className="w-3.5 h-3.5 rounded-full bg-accent-amber" />
                <div className="w-3.5 h-3.5 rounded-full bg-bg-dark border-l border-bg-cream" />
              </div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40">Expansion Path</span>
            </div>
            <h2 className="font-figtree font-black text-4xl md:text-5xl text-bg-dark tracking-tight">
              From one city <span className="text-accent-amber italic">to a continent.</span>
            </h2>
          </div>

          <div className="relative">
            {/* connector line */}
            <div className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-accent-amber/30 to-transparent" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
              {phases.map((p, i) => (
                <div key={p.phase}
                  className={`relative transition-all duration-700 ${phaseInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${i * 100}ms` }}>
                  <div className="w-10 h-10 rounded-full bg-bg-dark border border-bg-dark flex items-center justify-center mx-auto mb-5 font-bold text-accent-amber text-xs relative z-10">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div className="bg-white border border-border rounded-2xl p-6 shadow-card text-center">
                    <span className="text-xs text-accent-amber uppercase tracking-widest">{p.phase}</span>
                    <h3 className="font-figtree font-black text-base text-bg-dark mt-1 mb-2">{p.name}</h3>
                    <p className="font-exo text-xs text-neutral-500 leading-relaxed mb-3">{p.desc}</p>
                    <span className="inline-block text-xs text-neutral-400 border border-border rounded-full px-3 py-1">{p.period}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* closing statement */}
          <div className="bg-bg-dark rounded-2xl p-10 text-center relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-48 bg-accent-amber/5 rounded-full blur-3xl pointer-events-none" />
            <p className="font-exo text-white/60 text-base leading-relaxed max-w-2xl mx-auto mb-6 relative z-10">
              First-mover advantage in infrastructure markets is durable. The company that owns the swap stations, the fleet relationships, and the brand trust in each city will be structurally difficult to displace. <span className="text-white">Azura is moving in 2026 precisely because the competitive landscape in Nigeria's mid-tier cities is still uncrowded.</span>
            </p>
            <div className="flex flex-wrap justify-center gap-3 relative z-10">
              <Link to="/opportunity"
                className="inline-flex items-center gap-2 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all shadow-amber">
                View The Opportunity <ArrowRight size={13} />
              </Link>
              <a href="/documents/azura-whitepaper.pdf" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 border-2 border-white/15 text-white px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:border-white/30 hover:bg-white/5 transition-all">
                Download White Paper ↗
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default WhyAfrica;